/**
 * ImportExportPage — CSV import + export for contacts / companies / deals.
 * Import: paste/upload CSV, preview parses, execute writes via
 * invokeVaultEntity per row. Export downloads CSV streamed by the
 * server.
 */

import { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { api } from '@/lib/api';

interface Props {
  holderId: string;
}

type EntityType = 'contact' | 'company' | 'deal';

export function ImportExportPage({ holderId }: Props) {
  const [entityType, setEntityType] = useState<EntityType>('contact');
  const [csvText, setCsvText] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState<{
    count: number;
    issues: string[];
    rows: Array<Record<string, string>>;
  } | null>(null);
  const [result, setResult] = useState<{
    results: Array<{ index: number; ok: boolean; id?: string; error?: string }>;
  } | null>(null);
  const [exporting, setExporting] = useState(false);

  function parseCsv(text: string): Array<Record<string, string>> {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = splitCsvLine(lines[0]);
    return lines.slice(1).map((line) => {
      const cells = splitCsvLine(line);
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => {
        obj[h] = cells[i] ?? '';
      });
      return obj;
    });
  }

  function splitCsvLine(line: string): string[] {
    // Simple CSV split (no escaped quotes / commas inside quotes).
    return line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
  }

  const handlePreview = async () => {
    const rows = parseCsv(csvText);
    if (rows.length === 0) {
      setPreview({ count: 0, issues: ['No rows parsed from CSV.'], rows: [] });
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const p = await api.fiasLeadsImportPreview(holderId, entityType, rows);
      setPreview(p);
    } catch (err) {
      setPreview({ count: 0, issues: [(err as Error).message], rows: [] });
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    const rows = parseCsv(csvText);
    if (rows.length === 0) return;
    setExecuting(true);
    try {
      const r = await api.fiasLeadsImportExecute(holderId, entityType, rows);
      setResult(r);
    } catch (err) {
      setResult({ results: [{ index: 0, ok: false, error: (err as Error).message }] });
    } finally {
      setExecuting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const csv = await api.fiasLeadsExport(holderId, entityType);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fias-leads-${entityType}s.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Import / Export</h1>
        <p className="text-sm text-muted-foreground">
          CSV import writes to Vault via the substrate (one audit row per item). Export streams from
          the substrate.
        </p>
      </header>

      <div className="flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Entity</label>
        <select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value as EntityType);
            setPreview(null);
            setResult(null);
          }}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="contact">Contacts</option>
          <option value="company">Companies</option>
          <option value="deal">Deals</option>
        </select>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="ml-auto inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting…' : `Export ${entityType}s`}
        </button>
      </div>

      <div className="rounded border border-border bg-card p-4 flex flex-col gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">Import CSV</h2>
        <label className="inline-flex items-center gap-2 text-sm rounded border border-border bg-background px-3 py-2 w-fit cursor-pointer">
          <Upload className="h-4 w-4" />
          Choose file
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="hidden"
          />
        </label>
        <textarea
          placeholder={`Or paste CSV here. Header row required.\nContacts: first_name, last_name, primary_email, primary_phone, title, notes\nCompanies: name, domain, industry, website, description\nDeals: pipeline_id, stage_id, name, value, currency, contact_id, company_id`}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={8}
          className="rounded border border-border bg-background px-3 py-2 text-sm font-mono"
        />
        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            disabled={previewing || !csvText.trim()}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            {previewing ? 'Parsing…' : 'Preview'}
          </button>
          <button
            onClick={handleExecute}
            disabled={executing || !csvText.trim() || !preview || preview.issues.length > 0}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
          >
            {executing ? 'Importing…' : `Import ${preview?.count ?? '…'} rows`}
          </button>
        </div>
      </div>

      {preview && (
        <div className="rounded border border-border bg-card p-4">
          <h2 className="text-sm font-medium text-muted-foreground mb-2">
            Preview ({preview.count} rows)
          </h2>
          {preview.issues.length > 0 && (
            <ul className="text-xs text-rose-700 mb-2 list-disc list-inside">
              {preview.issues.map((i, idx) => (
                <li key={idx}>{i}</li>
              ))}
            </ul>
          )}
          <details>
            <summary className="text-xs cursor-pointer text-muted-foreground">
              Show first 5 rows
            </summary>
            <pre className="mt-2 rounded bg-muted/30 p-2 text-xs whitespace-pre-wrap">
              {JSON.stringify(preview.rows.slice(0, 5), null, 2)}
            </pre>
          </details>
        </div>
      )}

      {result && (
        <div className="rounded border border-border bg-card p-4">
          <h2 className="text-sm font-medium text-muted-foreground mb-2">Import result</h2>
          <p className="text-sm">
            Success: {result.results.filter((r) => r.ok).length} / {result.results.length}
          </p>
          {result.results.some((r) => !r.ok) && (
            <ul className="mt-2 text-xs text-rose-700 list-disc list-inside max-h-48 overflow-auto">
              {result.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.index}>
                    Row {r.index + 1}: {r.error}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
